import { useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { BookOpen, Globe, Plus } from 'lucide-react'
import { useSites } from '@/hooks/useSites'
import { usePermissions } from '@/hooks/usePermissions'

export function BlogRedirectPage() {
  const { data: sites = [], isLoading } = useSites()
  const navigate = useNavigate()
  const { canAccessSites } = usePermissions()

  useEffect(() => {
    if (!isLoading && sites.length === 1) {
      navigate(`/sites/${sites[0]!.id}/blog`, { replace: true })
    }
  }, [isLoading, sites, navigate])

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-violet-600 border-t-transparent" />
      </div>
    )
  }

  if (sites.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <BookOpen className="mb-4 h-12 w-12 text-gray-200" />
        <h2 className="text-lg font-semibold text-gray-900">No sites yet</h2>
        {canAccessSites ? (
          <>
            <p className="mt-1 text-sm text-gray-500">Create a site first, then manage its blog posts.</p>
            <Link
              to="/sites"
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 transition-colors"
            >
              <Plus className="h-4 w-4" /> Create a site
            </Link>
          </>
        ) : (
          <p className="mt-1 text-sm text-gray-500">Ask an admin to create a site — you'll be able to write posts once one exists.</p>
        )}
      </div>
    )
  }

  return (
    <div className="p-8">
      <div className="mb-6 flex items-center gap-3">
        <BookOpen className="h-6 w-6 text-violet-600" />
        <h1 className="text-2xl font-bold text-gray-900">Blog</h1>
      </div>
      <p className="mb-6 text-sm text-gray-500">Select a site to manage its blog posts:</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {sites.map((site) => (
          <Link
            key={site.id}
            to={`/sites/${site.id}/blog`}
            className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 transition-all hover:border-violet-300 hover:shadow-sm"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-50">
              <Globe className="h-5 w-5 text-violet-500" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 truncate">{site.name}</p>
              <p className="text-xs text-gray-400 truncate">{site.subdomain}.brix.app</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}
